import { useState } from 'preact/hooks'
import { AccountAvailableBalance, RewardsToClaim } from './AccountAvailableBalance'
import { AccountBannerTransitions } from './AccountBannerTransaction'
import { AccountChecklistStatic } from './AccountChecklist'
import { useAccountLayoutContext } from './AccountProfileContext'
import { AccountRewardsHistory, handleExpiryDate } from './AccountRewardsHistory'
import { AccountYourFitClub } from './AccountYourFitClub'
import { getRemainingDaysOnAssignment } from '../helpers/getRemainingDates'
import { Page } from './AccountPage'
import { useProfile } from '../hooks/useProfile'
import { useToggle } from '../hooks/useToggle'
import { AssignShoesModal } from './AssignShoesModal'

export const AccountFitClubLoyalty = () => {
  const { isActive, handleToggle } = useToggle(false)
  const { data: fitClubData, fetchData: getAccountFitClubData } = useFitClubData()

  return (
    <Page
      props={{
        page: {
          title: 'My Rewards'
        },
        suspense: {
          asyncCallback: getAccountFitClubData,
          loadingState: (
            <div className="space-y-8 md:space-y-10 perspective-0">
              <div className="h-[475px] animate-pulse bg-primary-grey md:h-[301px]" />
              <div className="h-[340px] -rotate-1 animate-pulse bg-primary-grey md:h-[330px]" />
            </div>
          )
        }
      }}
    >
      {fitClubData?.card?.code && (
        <AccountYourFitClub
          card={{
            id: fitClubData?.card?.code,
            memberSince: fitClubData?.card?.memberSince,
            balance: fitClubData?.balances?.reward_balance
          }}
        />
      )}
      <RewardBlocks fitClubData={fitClubData} />
      <AccountChecklistStatic
        children={fitClubData?.children || []}
        unassignedTransactionItems={fitClubData?.unassignedTransactionItems}
        isActive={isActive}
        handleToggle={handleToggle}
      />
      <AccountRewardsHistory props={{ rewards: fitClubData?.rewards }} />
      <AccountBannerTransitions />
    </Page>
  )
}

export const useFitClubData = () => {
  const { omneoClient, cacheConfig } = useAccountLayoutContext()
  const { getUnassignedItems, getUnexpiredUnassignedItems } = useProfile()

  const [data, setData] = useState(null)

  const getPageData = async () => {
    const profile = cacheConfig?.cacheData?.Profiles
    const rewardsPayload = cacheConfig?.cacheData?.Rewards
    const balances = cacheConfig?.cacheData?.Balance

    const [unassignedTransactionItems, unexpiredUnassignedTransactionItems, children] = await Promise.all([
      getUnassignedItems(true),
      getUnexpiredUnassignedItems(true),
      omneoClient.profileClient.getChildren()
    ])

    return {
      profile,
      rewardsPayload,
      balances,
      unassignedTransactionItems,
      unexpiredUnassignedTransactionItems,
      children
    }
  }

  const fetchData = async () => {
    const { profile, rewardsPayload, balances, unassignedTransactionItems, unexpiredUnassignedTransactionItems, children } = await getPageData()

    const memberSince = new Date(profile?.created_at)
    const code = profile?.identities?.find((identity) => identity?.handle === 'ap21_card')
    const unassignedTransactionItemsCount = unassignedTransactionItems.length
    const unexpiredUnassignedTransactionItemsCount = unexpiredUnassignedTransactionItems.length
    const unassignedTransactionItemsTotal = unassignedTransactionItemsCount * 2
    const unexpiredUnassignedTransactionItemsTotal = unexpiredUnassignedTransactionItemsCount * 2

    const [oldestRewardToExpiry] = rewardsPayload
      .filter((r) => r.is_active && !r.is_expired)
      .sort((a, b) => new Date(a.expires_at) - new Date(b.expires_at))
    const daysUntilRewardExpiryDate = oldestRewardToExpiry
      ? handleExpiryDate(oldestRewardToExpiry.expires_at).remainingDays
      : 0

    const [oldestLineItem] = unassignedTransactionItems.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    const daysUntilExpiryDate = oldestLineItem ? getRemainingDaysOnAssignment(oldestLineItem.created_at) : 0

    const [oldestUnexpiredLineItem] = unexpiredUnassignedTransactionItems.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    const daysUntilUnexpiredExpiryDate = oldestUnexpiredLineItem ? getRemainingDaysOnAssignment(oldestUnexpiredLineItem.created_at) : 0

    setData({
      card: {
        memberSince: memberSince.getFullYear(),
        code: code?.identifier
      },
      rewards: [...rewardsPayload],
      daysUntilRewardExpiryDate,
      balances,
      unassignedTransactionItems: {
        unassignedTransactionItemsCount,
        unassignedTransactionItemsTotal,
        daysUntilExpiryDate,
        items: unassignedTransactionItems
      },
      unexpiredUnassignedTransactionItems: {
        unexpiredUnassignedTransactionItemsCount,
        unexpiredUnassignedTransactionItemsTotal,
        daysUntilUnexpiredExpiryDate,
        items: unexpiredUnassignedTransactionItems
      },
      children
    })
  }

  return { data, fetchData }
}

export const RewardBlocks = ({ fitClubData }) => {
  const { isActive, handleToggle } = useToggle(false)
  const { unassignedTransactionItems, unexpiredUnassignedTransactionItems } = fitClubData || {}

  return (
    <>
      <div
        className={`grid gap-6 md:gap-8 perspective-0 ${
          unassignedTransactionItems?.items?.length ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'
        }`}
      >
        <AccountAvailableBalance
          daysUntilRewardExpiryDate={fitClubData.daysUntilRewardExpiryDate}
          balance={fitClubData.balances.reward_balance}
        />
        {!!unassignedTransactionItems?.items?.length && (
          <AssignShoesModal
            childrenProfiles={fitClubData.children}
            unassignedItems={unassignedTransactionItems.items}
            isActive={isActive}
            handleToggle={handleToggle}
            handleRefetch={() => {
              window.location.reload()
            }}
          >
            <RewardsToClaim unexpiredTransactionItems={unexpiredUnassignedTransactionItems} unassignedTransactionItems={unassignedTransactionItems} handleToggle={handleToggle} />
          </AssignShoesModal>
        )}
      </div>
    </>
  )
}
